import { create } from 'zustand';
import type { MediaConnectionQuality, MediaConnectionState } from '@matchroom/shared';
import { mediaLogger } from '@/services/media/mediaLogger';
import { splitStreamByTrackType } from '@/services/media/screenShareUtils';

export interface RemoteMediaState {
  userId: string;
  stream: MediaStream | null;
  streamRevision: number;
  micEnabled: boolean;
  cameraEnabled: boolean;
  screenEnabled: boolean;
  screenShareSignaled: boolean;
  speaking: boolean;
  connectionState: MediaConnectionState;
  connectionQuality: MediaConnectionQuality;
  cameraLoading: boolean;
}

interface MediaStoreState {
  isInitialized: boolean;
  localStream: MediaStream | null;
  micEnabled: boolean;
  cameraEnabled: boolean;
  screenEnabled: boolean;
  speaking: boolean;
  connectionState: MediaConnectionState;
  connectionQuality: MediaConnectionQuality;
  permissionError: 'camera' | 'microphone' | 'both' | 'secure-context' | null;
  mediaError: string | null;
  remoteMedia: Record<string, RemoteMediaState>;
  cameraPreviewStream: MediaStream | null;
  isFullscreen: boolean;
  screenThumbnailsVisible: boolean;
  showDeviceSettings: boolean;
  mediaRetryKey: number;

  setInitialized: (v: boolean) => void;
  setLocalStream: (stream: MediaStream | null) => void;
  setCameraPreviewStream: (stream: MediaStream | null) => void;
  setMicEnabled: (v: boolean) => void;
  setCameraEnabled: (v: boolean) => void;
  setScreenEnabled: (v: boolean) => void;
  setSpeaking: (v: boolean) => void;
  setConnectionState: (v: MediaConnectionState) => void;
  setConnectionQuality: (v: MediaConnectionQuality) => void;
  setPermissionError: (v: 'camera' | 'microphone' | 'both' | 'secure-context' | null) => void;
  setMediaError: (v: string | null) => void;
  setRemoteStream: (userId: string, stream: MediaStream) => void;
  removeRemote: (userId: string) => void;
  updateRemote: (userId: string, partial: Partial<RemoteMediaState>) => void;
  setFullscreen: (v: boolean) => void;
  setScreenThumbnailsVisible: (v: boolean) => void;
  setShowDeviceSettings: (v: boolean) => void;
  retryMedia: () => void;
  reset: () => void;
}

const remoteScreenWatchers = new Map<string, () => void>();

function clearRemoteScreenWatcher(userId: string): void {
  const cleanup = remoteScreenWatchers.get(userId);
  if (cleanup) {
    cleanup();
    remoteScreenWatchers.delete(userId);
  }
}

function watchRemoteStreamForScreenShare(
  userId: string,
  stream: MediaStream,
  get: () => MediaStoreState,
  set: (partial: Partial<MediaStoreState> | ((state: MediaStoreState) => Partial<MediaStoreState>)) => void,
): void {
  clearRemoteScreenWatcher(userId);

  const reevaluate = () => {
    const split = splitStreamByTrackType(stream);
    const hasScreen = !!split.screen?.getVideoTracks().some((t) => t.readyState === 'live');
    if (!hasScreen) return;
    const prev = get().remoteMedia[userId];
    if (prev?.screenEnabled) return;
    set({
      remoteMedia: {
        ...get().remoteMedia,
        [userId]: {
          ...(prev ?? defaultRemote(userId)),
          screenEnabled: true,
          screenShareSignaled: true,
        },
      },
    });
  };

  stream.addEventListener('addtrack', reevaluate);
  for (const track of stream.getVideoTracks()) {
    track.addEventListener('unmute', reevaluate);
  }
  reevaluate();

  remoteScreenWatchers.set(userId, () => {
    stream.removeEventListener('addtrack', reevaluate);
    for (const track of stream.getVideoTracks()) {
      track.removeEventListener('unmute', reevaluate);
    }
  });
}

const defaultRemote = (userId: string): RemoteMediaState => ({
  userId,
  stream: null,
  streamRevision: 0,
  micEnabled: true,
  cameraEnabled: false,
  screenEnabled: false,
  screenShareSignaled: false,
  speaking: false,
  connectionState: 'connecting',
  connectionQuality: 'unknown',
  cameraLoading: false,
});

export const useMediaStore = create<MediaStoreState>((set, get) => ({
  isInitialized: false,
  localStream: null,
  micEnabled: true,
  cameraEnabled: true,
  screenEnabled: false,
  speaking: false,
  connectionState: 'connecting',
  connectionQuality: 'unknown',
  permissionError: null,
  mediaError: null,
  remoteMedia: {},
  cameraPreviewStream: null,
  isFullscreen: false,
  screenThumbnailsVisible: true,
  showDeviceSettings: false,
  mediaRetryKey: 0,

  setInitialized: (isInitialized) => set({ isInitialized }),
  setLocalStream: (localStream) =>
    set({
      localStream,
      ...(localStream?.getTracks().some((t) => t.readyState === 'live') ? { permissionError: null } : {}),
    }),
  setCameraPreviewStream: (cameraPreviewStream) => set({ cameraPreviewStream }),
  setMicEnabled: (micEnabled) => set({ micEnabled }),
  setCameraEnabled: (cameraEnabled) => set({ cameraEnabled }),
  setScreenEnabled: (screenEnabled) => set({ screenEnabled }),
  setSpeaking: (speaking) => set({ speaking }),
  setConnectionState: (connectionState) => set({ connectionState }),
  setConnectionQuality: (connectionQuality) => set({ connectionQuality }),
  setPermissionError: (permissionError) => set({ permissionError }),
  setMediaError: (mediaError) => set({ mediaError }),
  setRemoteStream: (userId, stream) => {
    const prev = get().remoteMedia[userId];
    const screenShareSignaled = prev?.screenShareSignaled ?? false;
    const split = splitStreamByTrackType(stream, { screenShareSignaled });
    const hasCamera =
      split.camera?.getVideoTracks().some((t) => t.enabled && t.readyState === 'live') ?? false;
    const hasScreen =
      split.screen?.getVideoTracks().some((t) => t.readyState === 'live') ?? false;
    const hasAudio =
      split.audio?.getAudioTracks().some((t) => t.enabled && t.readyState === 'live') ??
      stream.getAudioTracks().some((t) => t.enabled && t.readyState === 'live');
    const screenEnabled = (prev?.screenShareSignaled ?? false) || hasScreen;

    set({
      remoteMedia: {
        ...get().remoteMedia,
        [userId]: {
          ...(prev ?? defaultRemote(userId)),
          stream,
          streamRevision: (prev?.streamRevision ?? 0) + 1,
          cameraLoading: false,
          cameraEnabled: hasCamera,
          screenEnabled,
          screenShareSignaled: prev?.screenShareSignaled ?? hasScreen,
          micEnabled: hasAudio,
          connectionState: 'connected',
        },
      },
      permissionError: null,
    });
    watchRemoteStreamForScreenShare(userId, stream, get, set);
  },
  removeRemote: (userId) => {
    clearRemoteScreenWatcher(userId);
    const remoteMedia = { ...get().remoteMedia };
    delete remoteMedia[userId];
    set({ remoteMedia });
  },
  updateRemote: (userId, partial) =>
    set({
      remoteMedia: {
        ...get().remoteMedia,
        [userId]: { ...(get().remoteMedia[userId] ?? defaultRemote(userId)), ...partial },
      },
    }),
  setFullscreen: (isFullscreen) => set({ isFullscreen }),
  setScreenThumbnailsVisible: (screenThumbnailsVisible) => set({ screenThumbnailsVisible }),
  setShowDeviceSettings: (showDeviceSettings) => set({ showDeviceSettings }),
  retryMedia: () =>
    set((s) => {
      mediaLogger.retry({ mediaRetryKey: s.mediaRetryKey + 1 });
      return {
        mediaRetryKey: s.mediaRetryKey + 1,
        isInitialized: false,
        permissionError: null,
        mediaError: null,
      };
    }),
  reset: () =>
    set((s) => {
      for (const userId of remoteScreenWatchers.keys()) {
        clearRemoteScreenWatcher(userId);
      }
      return {
        isInitialized: false,
        localStream: null,
        cameraPreviewStream: null,
        micEnabled: true,
        cameraEnabled: true,
        screenEnabled: false,
        speaking: false,
        connectionState: 'connecting',
        connectionQuality: 'unknown',
        permissionError: null,
        mediaError: null,
        remoteMedia: {},
        isFullscreen: false,
        screenThumbnailsVisible: true,
        showDeviceSettings: false,
        mediaRetryKey: s.mediaRetryKey,
      };
    }),
}));
